import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import Pagination from '@/components/ui/Pagination';
import { Eye, Edit, Trash2, Search, Plus, Users, UserCheck, UserX, Shield, UserCog } from 'lucide-react';
import { userApi, ROLE_LABELS } from '../../api/userApi';
import type { User, UserFilters } from '../../api/userApi';
import { useToast } from '@/hooks/use-toast';
import UserForm from '../../pages/users/UserForm';
// import UserDetails from '../../pages/users/UserDetails';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export const UsersTab: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    role: '',
  });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userStats, setUserStats] = useState({
    total: 0,
    admin: 0,
    manager: 0,
    user: 0,
    super_admin: 0
  });
  const [updatingUsers, setUpdatingUsers] = useState<Set<number>>(new Set());

  const { toast } = useToast();

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, filters]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params: UserFilters = {
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'created_at',
        sortOrder: 'desc'
      };

      if (filters.search) params.search = filters.search;
      if (filters.role) params.role = filters.role;

      const response = await userApi.getUsers(params);
      setUsers(response.data.users);
      setPagination(response.data.pagination);

      // Calculate stats from current data
      const stats = {
        total: response.data.pagination.total,
        admin: response.data.users.filter((u: User) => u.role === 'admin').length,
        manager: response.data.users.filter((u: User) => u.role === 'manager').length,
        user: response.data.users.filter((u: User) => u.role === 'user').length,
        super_admin: response.data.users.filter((u: User) => u.role === 'super_admin').length
      };
      setUserStats(stats);
    } catch (error: any) {
      console.error('Failed to fetch users:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch users',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (key: string, value: string) => {
    // Convert "all" to empty string for API
    const filterValue = value === 'all' ? '' : value;
    setFilters(prev => ({ ...prev, [key]: filterValue }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setPagination(prev => ({ ...prev, limit: itemsPerPage, page: 1 }));
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setShowCreateModal(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await userApi.deleteUser(selectedUser.id.toString());
      toast({
        title: 'Success',
        description: 'User deleted successfully',
      });
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleUserSaved = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedUser(null);
    fetchUsers();
  };

  const handleRoleCardClick = (role: string) => {
    // If already filtering by this role, clear the filter
    if (filters.role === role) {
      setFilters(prev => ({ ...prev, role: '' }));
    } else {
      setFilters(prev => ({ ...prev, role }));
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleUpdateUserRole = async (userId: number, newRole: string) => {
    setUpdatingUsers(prev => new Set(prev).add(userId));
    try {
      await userApi.updateUser(userId.toString(), { role: newRole });
      toast({
        title: 'Success',
        description: 'User role updated successfully',
      });
      fetchUsers();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };



  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-purple-100 text-purple-800';
      case 'MANAGER':
        return 'bg-blue-100 text-blue-800';
      case 'EMPLOYEE':
        return 'bg-green-100 text-green-800';
      case 'VIEWER':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <Card
          className={`bg-blue-50 dark:bg-blue-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer transition-all hover:shadow-xl hover:scale-105 ${filters.role === '' ? 'ring-2 ring-blue-500' : ''
            }`}
          onClick={() => handleRoleCardClick('')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Total Users</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">{userStats.total}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Click to show all</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-full">
                <Users className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`bg-purple-50 dark:bg-purple-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer transition-all hover:shadow-xl hover:scale-105 ${filters.role === 'super_admin' ? 'ring-2 ring-purple-500' : ''
            }`}
          onClick={() => handleRoleCardClick('super_admin')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Super Admins</p>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{userStats.super_admin}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Click to filter</p>
              </div>
              <div className="bg-purple-500 p-3 rounded-full">
                <Shield className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`bg-green-50 dark:bg-green-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer transition-all hover:shadow-xl hover:scale-105 ${filters.role === 'admin' ? 'ring-2 ring-green-500' : ''
            }`}
          onClick={() => handleRoleCardClick('admin')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Admins</p>
                <p className="text-3xl font-bold text-green-600 dark:text-green-400">{userStats.admin}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Click to filter</p>
              </div>
              <div className="bg-green-500 p-3 rounded-full">
                <UserCheck className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`bg-blue-50 dark:bg-blue-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer transition-all hover:shadow-xl hover:scale-105 ${filters.role === 'manager' ? 'ring-2 ring-blue-500' : ''
            }`}
          onClick={() => handleRoleCardClick('manager')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Managers</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{userStats.manager}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Click to filter</p>
              </div>
              <div className="bg-blue-500 p-3 rounded-full">
                <Users className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`bg-gray-50 dark:bg-gray-900/20 border-0 shadow-lg dark:border dark:border-gray-700 cursor-pointer transition-all hover:shadow-xl hover:scale-105 ${filters.role === 'user' ? 'ring-2 ring-gray-500' : ''
            }`}
          onClick={() => handleRoleCardClick('user')}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">Users</p>
                <p className="text-3xl font-bold text-gray-600 dark:text-gray-400">{userStats.user}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Click to filter</p>
              </div>
              <div className="bg-gray-500 p-3 rounded-full">
                <UserX className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card className="bg-white dark:bg-gray-800 shadow-lg border-0 rounded-2xl dark:border dark:border-gray-700">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-semibold text-gray-900 dark:text-white">User Management</CardTitle>
            <Button onClick={handleCreateUser} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search users..."
                value={filters.search}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
              />
            </div>
            <Select value={filters.role || 'all'} onValueChange={(value) => handleFilterChange('role', value)}>
              <SelectTrigger className="w-full md:w-48 h-12 border-gray-200 focus:border-blue-500 focus:ring-blue-500 rounded-xl">
                <SelectValue placeholder="Filter by Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {Object.entries(ROLE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

          </div>

          {/* Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 dark:bg-gray-700">
                      <TableHead className="font-semibold text-gray-900 dark:text-white">User</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Role</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Created At</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Updated At</TableHead>
                      <TableHead className="font-semibold text-gray-900 dark:text-white">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  {user.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge className={getRoleBadgeColor(user.role)}>
                              <Shield className="w-3 h-3 mr-1" />
                              {ROLE_LABELS[user.role as keyof typeof ROLE_LABELS] || user.role}
                            </Badge>
                            <Select
                              value={user.role}
                              onValueChange={(newRole) => handleUpdateUserRole(user.id, newRole)}
                              disabled={updatingUsers.has(user.id)}
                            >
                              <SelectTrigger className="w-8 h-8 p-0 border-0 bg-transparent hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50">
                                {updatingUsers.has(user.id) ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500"></div>
                                ) : (
                                  <UserCog className="h-4 w-4 text-gray-500" />
                                )}
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(ROLE_LABELS).map(([roleValue, roleLabel]) => (
                                  <SelectItem key={roleValue} value={roleValue}>
                                    <div className="flex items-center gap-2">
                                      <Shield className="w-4 h-4" />
                                      {roleLabel}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                        <TableCell>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {new Date(user.updatedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewUser(user)}
                              className="h-8 w-8 p-0 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4 text-blue-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditUser(user)}
                              className="h-8 w-8 p-0 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(user)}
                              className="h-8 w-8 p-0 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {users.length === 0 && (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-500 dark:text-gray-400">No users found</p>
                  {(filters.search || filters.role) && (
                    <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try adjusting your search or filters</p>
                  )}
                </div>
              )}

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.pages}
                  totalItems={pagination.total}
                  itemsPerPage={pagination.limit}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  showItemsPerPage={true}
                  showCurrentRange={true}
                  maxVisiblePages={5}
                  className="mt-6"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <UserForm
            onSuccess={handleUserSaved}
            onCancel={() => setShowCreateModal(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <UserForm
            user={selectedUser}
            onSuccess={handleUserSaved}
            onCancel={() => setShowEditModal(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <p className="text-gray-900">{selectedUser.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{selectedUser.email}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <p className="text-gray-900">{ROLE_LABELS[selectedUser.role as keyof typeof ROLE_LABELS] || selectedUser.role}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Created At</label>
                  <p className="text-gray-900">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
                  Close
                </Button>
                <Button onClick={() => {
                  setShowDetailsModal(false);
                  handleEditUser(selectedUser);
                }}>
                  Edit User
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-600">
              Are you sure you want to delete <strong>{selectedUser?.name}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteUser}
              >
                Delete User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};